import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Anchor, Breadcrumbs, Button, Center, Group, Loader, PasswordInput, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ApiError, cloudApi } from '../../api';
import { formatBytes, formatDateTime } from '../../format';
import { Section } from '../../ui/Section';

/**
 * A folder as a line drawing in the accent colour, the size of the text
 * beside it - instead of the colourful emoji, which ignored theme and size.
 */
function FolderIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-label="Ordner" role="img" style={{ display: 'block', color: 'var(--mantine-primary-color-filled)' }}>
      <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4.2l2 2.2h8.8A1.5 1.5 0 0 1 21 9.7v8.8A1.5 1.5 0 0 1 19.5 20h-15A1.5 1.5 0 0 1 3 18.5z" />
    </svg>
  );
}

/** A plain sign for the kind of file - no icon set, just enough to scan the list. */
function kind(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'PDF';
  if (['dwg', 'dxf', 'ifc', 'rvt', 'pln', 'vwx', '3dm', 'skp'].includes(ext)) return 'CAD';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'tif', 'tiff'].includes(ext)) return 'Bild';
  if (['xlsx', 'xls', 'ods', 'csv'].includes(ext)) return 'Tab.';
  if (['docx', 'doc', 'odt', 'rtf', 'txt', 'md'].includes(ext)) return 'Text';
  if (['zip', '7z', 'rar'].includes(ext)) return 'Zip';
  return ext ? ext.slice(0, 4).toUpperCase() : '·';
}

/**
 * The project's folder in Nextcloud, read through a public share link: folders
 * to click through, files with size and date. A click opens Nextcloud; PST
 * keeps no copy. Setting the link needs write permission.
 */
export function FilesPage(): JSX.Element {
  const { projectId = '' } = useParams();
  const queryClient = useQueryClient();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canEdit = me?.permissions.includes('projectdata.write') ?? false;
  const config = useQuery({ queryKey: ['cloud', projectId], queryFn: () => cloudApi.get(projectId) });
  const [path, setPath] = useState('/');
  const listing = useQuery({
    queryKey: ['cloud', projectId, 'list', path],
    queryFn: () => cloudApi.list(projectId, path),
    enabled: config.data?.configured === true,
    retry: false,
    staleTime: 30_000,
  });
  const [editing, setEditing] = useState(false);

  if (config.isPending) return <Center h={200}><Loader /></Center>;
  if (config.isError) return <Alert color="red">Der Projektordner konnte nicht geladen werden.</Alert>;
  const c = config.data;

  if (!c.configured || editing) {
    return (
      <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        <SetupForm projectId={projectId} current={c} canEdit={canEdit} onDone={() => { setEditing(false); setPath('/'); }} onCancel={c.configured ? () => setEditing(false) : undefined} />
      </div>
    );
  }

  const parts = path.split('/').filter(Boolean);
  const crumbs = [{ label: 'Projektordner', path: '/' }, ...parts.map((p, i) => ({ label: p, path: `/${parts.slice(0, i + 1).join('/')}` }))];
  const error = listing.error instanceof ApiError ? listing.error.message : listing.isError ? 'Der Ordner ließ sich nicht lesen.' : null;

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        <Section
          title="Dateien"
          description="Der Projektordner in Nextcloud, nur lesend. Ein Klick öffnet die Datei in Nextcloud – ohne Freigabe-Passwort, sofern Sie dort angemeldet sind. ⤓ lädt über PST herunter, Ordner als ZIP; ↗ öffnet die Freigabe (für Personen ohne Nextcloud-Konto). PST speichert keine Dateien."
          actions={
            <>
              {listing.data && <Button size="xs" variant="default" component="a" href={listing.data.open} target="_blank" rel="noopener noreferrer">In Nextcloud öffnen</Button>}
              {listing.data && (
                <Tooltip label="Nextcloud packt den Ordner; bei großen Ordnern dauert der Beginn einen Moment.">
                  <Button size="xs" variant="default" component="a" href={`/api${listing.data.download}`} download>{path === '/' ? 'Ganzen Projektordner als ZIP' : 'Ordner als ZIP'}</Button>
                </Tooltip>
              )}
              <Button size="xs" variant="subtle" onClick={() => void listing.refetch()} loading={listing.isFetching}>Aktualisieren</Button>
              {canEdit && <Button size="xs" variant="subtle" color="gray" onClick={() => setEditing(true)}>Link ändern</Button>}
            </>
          }
        >
          <Breadcrumbs separator="›">
            {crumbs.map((b, i) => (i === crumbs.length - 1
              ? <Text key={b.path} size="sm" fw={600}>{b.label}</Text>
              : <Anchor key={b.path} size="sm" onClick={() => setPath(b.path)}>{b.label}</Anchor>))}
          </Breadcrumbs>
          {error && <Alert color="red" py={6}>{error}</Alert>}
          {listing.isPending && <Center h={80}><Loader size="sm" /></Center>}
          {listing.data && listing.data.entries.length === 0 && <Text size="sm" c="dimmed">Dieser Ordner ist leer.</Text>}
          {listing.data && listing.data.entries.length > 0 && (
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing={4} highlightOnHover className="pst-tabular">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={54} />
                    <Table.Th>Name</Table.Th>
                    <Table.Th w={110} ta="right">Größe</Table.Th>
                    <Table.Th w={170}>Geändert</Table.Th>
                    <Table.Th w={64} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {path !== '/' && (
                    <Table.Tr>
                      <Table.Td><Text size="xs" c="dimmed">↑</Text></Table.Td>
                      <Table.Td colSpan={4}><Anchor size="sm" onClick={() => setPath(path.slice(0, path.lastIndexOf('/')) || '/')}>eine Ebene höher</Anchor></Table.Td>
                    </Table.Tr>
                  )}
                  {listing.data.entries.map((e) => (
                    <Table.Tr key={e.path}>
                      <Table.Td>{e.folder ? <FolderIcon /> : <Text size="xs" c="dimmed" ff="monospace">{kind(e.name)}</Text>}</Table.Td>
                      <Table.Td>
                        {e.folder
                          ? (
                            <Group gap={6} wrap="nowrap">
                              <Anchor size="sm" fw={500} onClick={() => setPath(e.path)}>{e.name}</Anchor>
                              <Tooltip label="Ordner in Nextcloud öffnen">
                                <Anchor size="xs" c="dimmed" href={e.open} target="_blank" rel="noopener noreferrer" aria-label={`${e.name} in Nextcloud öffnen`}>Nextcloud ↗</Anchor>
                              </Tooltip>
                            </Group>
                          )
                          : <Anchor size="sm" href={e.open} target="_blank" rel="noopener noreferrer" title="In Nextcloud öffnen">{e.name}</Anchor>}
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm" c="dimmed">{e.folder ? '' : formatBytes(e.size)}</Text></Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{e.modified ? formatDateTime(e.modified) : ''}</Text></Table.Td>
                      <Table.Td>
                        <Group gap={2} wrap="nowrap" justify="flex-end">
                          <Tooltip label={e.folder ? 'Ordner als ZIP herunterladen (über PST)' : 'Herunterladen (über PST, ohne Passwortabfrage)'}>
                            <ActionIcon size="sm" variant="subtle" color="gray" component="a" href={`/api${e.download}`} download aria-label={e.folder ? `${e.name} als ZIP herunterladen` : `${e.name} herunterladen`}>⤓</ActionIcon>
                          </Tooltip>
                          <Tooltip label="In der Freigabe öffnen (ohne Nextcloud-Konto)">
                            <ActionIcon size="sm" variant="subtle" color="gray" component="a" href={e.share} target="_blank" rel="noopener noreferrer" aria-label={`${e.name} in der Freigabe öffnen`}>↗</ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Section>
      </Stack>
    </div>
  );
}

function SetupForm({ projectId, current, canEdit, onDone, onCancel }: {
  projectId: string;
  current: { configured: boolean; shareUrl: string | null; hasPassword: boolean };
  canEdit: boolean;
  onDone: () => void;
  onCancel?: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState(current.shareUrl ?? '');
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const save = useMutation({
    // An empty password field keeps the stored one - unless removing it was chosen.
    mutationFn: () => cloudApi.save(projectId, url.trim(), clearPassword ? null : password || (current.hasPassword ? undefined : null)),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['cloud', projectId] }); onDone(); },
  });
  const remove = useMutation({
    mutationFn: () => cloudApi.remove(projectId),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['cloud', projectId] }); onDone(); },
  });
  return (
    <Section title="Nextcloud-Projektordner verknüpfen" description="PST zeigt den Ordner hier an und verlinkt jede Datei nach Nextcloud – nur lesend, ohne Kopie.">
      <Stack gap="xs" maw={720}>
        <Text size="sm">So entsteht der Link in Nextcloud: den Projektordner öffnen → <b>Teilen</b> → <b>Link teilen</b> (+) → in den Einstellungen des Links „Nur lesen“ lassen, gern ein <b>Passwort</b> setzen → Link kopieren.</Text>
        {!canEdit && <Alert color="gray" py={6}>Den Link hinterlegt, wer Projektdaten bearbeiten darf.</Alert>}
        {save.isError && <Alert color="red" py={6}>{save.error instanceof ApiError ? save.error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        <form onSubmit={(e) => { e.preventDefault(); if (url.trim()) save.mutate(); }}>
          <Stack gap="xs">
            <TextInput label="Freigabelink" placeholder="https://cloud.example.org/s/…" value={url} onChange={(e) => setUrl(e.currentTarget.value)} disabled={!canEdit} data-autofocus />
            <PasswordInput
              label="Passwort der Freigabe"
              description={current.hasPassword ? 'Gespeichert. Leer lassen, um es zu behalten.' : 'Nur wenn die Freigabe eines hat.'}
              value={password} onChange={(e) => setPassword(e.currentTarget.value)} disabled={!canEdit || clearPassword} autoComplete="off"
            />
            {current.hasPassword && (
              <Anchor size="xs" component="button" type="button" onClick={() => setClearPassword((v) => !v)}>{clearPassword ? 'Passwort doch behalten' : 'gespeichertes Passwort entfernen'}</Anchor>
            )}
            <Group gap="xs">
              <Button type="submit" disabled={!canEdit || !url.trim()} loading={save.isPending}>Prüfen und speichern</Button>
              {onCancel && <Button variant="default" onClick={onCancel}>Abbrechen</Button>}
              {current.configured && canEdit && (
                <Button variant="subtle" color="red" loading={remove.isPending} onClick={() => { if (window.confirm('Verknüpfung lösen? In Nextcloud bleibt alles, wie es ist.')) remove.mutate(); }}>Verknüpfung lösen</Button>
              )}
            </Group>
            <Text size="xs" c="dimmed">Wer den Freigabelink hat, sieht den Ordner – wie bei jeder Nextcloud-Freigabe. Das Passwort speichert PST verschlüsselt und zeigt es nie wieder an.</Text>
          </Stack>
        </form>
      </Stack>
    </Section>
  );
}
